import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ElementSize,
  SchemaElement,
  SchemaRegion,
  TableShape,
} from "../../../../api/restaurantSchema";
import TableView from "../../../../components/restaurant-schema/TableView";
import { CREATE_SEAT_CHIPS, ELEMENT_SIZES } from "../../../../components/restaurant-schema/elementCatalog";
import { FLOOR_GRID_CLASS } from "../../../../components/restaurant-schema/floorGrid";
import { DESIGN_UNIT, MAX_SEATS } from "../../../../components/restaurant-schema/geometry";
import { CheckIcon, InfoIcon, TableIcon } from "../../../../components/restaurant-schema/schemaIcons";
import { hasTrailingStar, isTableNameTaken } from "../../../../components/restaurant-schema/tableNames";
import type { NewTableValues } from "../useSchemaEditorActions";
import { Field, SeatsField, Segmented, TableShapeTiles, TextField } from "./formControls";
import { GhostButton, PrimaryButton, SchemaModalShell } from "./SchemaModalShell";

// Превюто е в пикселите на дизайна: 1 логическа единица = 1/DESIGN_UNIT px.
const PREVIEW_SCALE = 1 / DESIGN_UNIT;

/**
 * „Нова маса" (дизайн 1b): живо превю със столовете, номер (следващият
 * свободен), брой места, форма и размер (Малка…XL върху плота, който расте с
 * местата). Позицията и завъртането се задават върху схемата. „Остави
 * прозореца отворен за следващата маса" държи модала отворен със следващия
 * номер — за бързо въвеждане; създадените в серията се изброяват в модала
 * (освен съобщението за всяка), за да е ясно какво е станало.
 */
const TableCreateModal = ({
  open,
  regions,
  suggestedName,
  onSubmit,
  onClose,
}: {
  open: boolean;
  // Всички региони — заетият номер се хваща още във формата (BE би дал 409).
  regions: SchemaRegion[];
  suggestedName: string;
  onSubmit: (values: NewTableValues) => Promise<SchemaElement | undefined>;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  // null = потребителят не е писал → полето следва предложението (кеша).
  const [typedName, setTypedName] = useState<string | null>(null);
  const [tableShape, setTableShape] = useState<TableShape>("round");
  const [seats, setSeats] = useState(4);
  const [size, setSize] = useState<ElementSize>("medium");
  const [addAnother, setAddAnother] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Номерата, създадени докато модалът е отворен (серията).
  const [createdNames, setCreatedNames] = useState<string[]>([]);
  const name = typedName ?? suggestedName;
  const nameError = hasTrailingStar(name)
    ? t("restaurantSchema.table.nameTrailingStar")
    : isTableNameTaken(regions, name)
      ? t("restaurantSchema.table.nameTaken")
      : null;
  const canSave = name.trim().length > 0 && nameError === null;

  const handleSave = async () => {
    if (!canSave || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const created = await onSubmit({ name: name.trim(), tableShape, seats, size });
      setCreatedNames((current) => [...current, created?.name ?? name.trim()]);
      // „Остави отворено": полето се връща към следващия свободен номер, дори
      // създаденото да не е цифрово („E2E1") и предложението да не се мени.
      if (addAnother) setTypedName(null);
      else onClose();
    } catch {
      // Грешката е показана от родителя; модалът остава отворен.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SchemaModalShell
      open={open}
      size="form"
      icon={<TableIcon className="h-5 w-5" />}
      title={t("restaurantSchema.tableCreate.title")}
      subtitle={t("restaurantSchema.tableCreate.subtitle")}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            role="checkbox"
            aria-checked={addAnother}
            onClick={() => setAddAnother((current) => !current)}
            className="flex cursor-pointer items-start gap-2 border-none bg-transparent p-0 text-left text-[13px] text-ink-2 sm:mr-auto"
          >
            <span
              className={`mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] text-white ${
                addAnother ? "border-brand bg-brand" : "border-shade-5 bg-surface"
              }`}
            >
              {addAnother && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="font-medium leading-[1.3]">{t("restaurantSchema.tableCreate.addAnother")}</span>
              <span className="text-[12px] leading-[1.35] text-muted">{t("restaurantSchema.tableCreate.addAnotherHint")}</span>
            </span>
          </button>
          <GhostButton onClick={onClose} disabled={isSubmitting}>
            {t("common.cancel")}
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={!canSave} loading={isSubmitting}>
            {t("restaurantSchema.tableCreate.submit")}
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-[18px]">
        {/* Същата милиметрова хартия като пода на схемата; карето е в мащаба на превюто. */}
        <div
          className={`relative flex h-[180px] items-center justify-center rounded-[12px] border border-shade-2 bg-[size:60px_60px,60px_60px,12px_12px,12px_12px] ${FLOOR_GRID_CLASS}`}
        >
          <TableView
            name={name.trim() || "—"}
            shape={tableShape}
            seats={seats}
            size={size}
            rotation={0}
            scale={PREVIEW_SCALE}
            selected={false}
          />
          <span className="absolute bottom-2 right-2.5 text-[11.5px] text-muted">
            {t("restaurantSchema.tableCreate.preview")}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t("restaurantSchema.table.number")}
            required
            hint={
              typedName === null
                ? t("restaurantSchema.tableCreate.numberHint")
                : t("restaurantSchema.tableCreate.numberHintTyped")
            }
            error={nameError}
          >
            <TextField
              value={name}
              onChange={setTypedName}
              onEnter={handleSave}
              placeholder={t("restaurantSchema.table.numberPlaceholder")}
              maxLength={50}
              autoFocus
            />
          </Field>
          <Field label={t("restaurantSchema.table.seats")}>
            <SeatsField
              value={seats}
              min={1}
              max={MAX_SEATS}
              chips={CREATE_SEAT_CHIPS}
              onChange={setSeats}
            />
          </Field>
        </div>

        <Field label={t("restaurantSchema.table.shape")}>
          <TableShapeTiles value={tableShape} onChange={setTableShape} />
        </Field>

        <Field label={t("restaurantSchema.table.size")}>
          <Segmented
            options={ELEMENT_SIZES.map((value) => ({
              value,
              label: t(`restaurantSchema.tableSizes.${value}`),
            }))}
            value={size}
            onChange={setSize}
          />
        </Field>

        <div className="flex items-center gap-2.5 rounded-[10px] bg-fill px-3 py-2.5 text-[12.5px] leading-[1.4] text-body">
          <InfoIcon className="h-[15px] w-[15px] shrink-0" />
          {t("restaurantSchema.tableCreate.placementHint")}
        </div>

        {createdNames.length > 0 && (
          <div className="flex items-start gap-2.5 rounded-[10px] bg-ok-tint px-3 py-2.5 text-[12.5px] leading-[1.4] text-ok-deep">
            <CheckIcon className="mt-0.5 h-[15px] w-[15px] shrink-0" strokeWidth={2.5} />
            <span>
              {t("restaurantSchema.tableCreate.createdSoFar", {
                count: createdNames.length,
                names: createdNames.join(" · "),
              })}
            </span>
          </div>
        )}
      </div>
    </SchemaModalShell>
  );
};

export default TableCreateModal;
